import React, { useRef, useState, useEffect } from 'react';
import './EditorToolbar.css';
import EditorDropdown from './EditorDropdown';

function EditorToolbar({ editor }) {
  const [, setUpdate] = useState(0);

  useEffect(() => {
    if (!editor) return;
    const handler = () => setUpdate(prev => prev + 1);
    editor.on('transaction', handler);
    return () => editor.off('transaction', handler);
  }, [editor]);

  const fileInputRef = useRef();
  const [showTableSelector, setShowTableSelector] = useState(false);
  const [rows, setRows] = useState(0);
  const [cols, setCols] = useState(0);
  const tableButtonRef = useRef();
  const imageButtonRef = useRef();

  const [selectorStyle, setSelectorStyle] = useState({ top: 0, left: 0 });
  const [imagePanelStyle, setImagePanelStyle] = useState({ top: 0, left: 0 });

  // 이미지 삽입 관련 상태
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [imageType, setImageType] = useState('file'); // 'file' or 'link'
  const [imageUrl, setImageUrl] = useState('');
  const [imageSize, setImageSize] = useState({ width: '', height: '' });

  useEffect(() => {
    if (showTableSelector && tableButtonRef.current) {
      const rect = tableButtonRef.current;
      setSelectorStyle({
        top: rect.offsetTop + rect.offsetHeight + 6,
        left: rect.offsetLeft,
      });
    }

    if (showImagePanel && imageButtonRef.current) {
      const rect = imageButtonRef.current;
      setImagePanelStyle({
        top: rect.offsetTop + rect.offsetHeight + 6,
        left: rect.offsetLeft,
      });
    }

  }, [showTableSelector, showImagePanel]);

  if (!editor) return null;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setImageUrl(base64); // base64 저장
    };
    reader.readAsDataURL(file);
  };

  const insertImage = () => {
    if (!imageUrl) return;

    editor.chain().focus().insertContent({
      type: 'resizableImage', // 커스텀 노드 이름
      attrs: {
        src: imageUrl,
        width: imageSize.width || 'auto',
        height: imageSize.height || 'auto',
        alt: '',
      },
    }).run();

    // 초기화
    setShowImagePanel(false);
    setImageUrl('');
    setImageSize({ width: '', height: '' });
  };
  const fontFamilyOptions = [
    { label: '글씨체', value: '', disabled: true },
    { label: '맑은 고딕', value: 'Malgun Gothic', style: { fontFamily: 'Malgun Gothic' } },
    { label: '나눔고딕', value: 'Nanum Gothic', style: { fontFamily: 'Nanum Gothic' } },
    { label: '굴림', value: 'Gulim', style: { fontFamily: 'Gulim' } },
    { label: '굴림체', value: 'GulimChe', style: { fontFamily: 'GulimChe' } },
    { label: '궁서', value: 'Gungsuh', style: { fontFamily: 'Gungsuh' } },
    { label: '궁서체', value: 'GungsuhChe', style: { fontFamily: 'GungsuhChe' } },
    { label: '돋움', value: 'Dotum', style: { fontFamily: 'Dotum' } },
    { label: '돋움체', value: 'DotumChe', style: { fontFamily: 'DotumChe' } },
    { label: '바탕', value: 'Batang', style: { fontFamily: 'Batang' } },
    { label: '바탕체', value: 'BatangChe', style: { fontFamily: 'BatangChe' } },
    { label: 'Arial', value: 'Arial', style: { fontFamily: 'Arial' } },
    { label: 'Tahoma', value: 'Tahoma', style: { fontFamily: 'Tahoma' } },
    { label: 'Verdana', value: 'Verdana', style: { fontFamily: 'Verdana' } },
    { label: 'Courier New', value: 'Courier New', style: { fontFamily: 'Courier New' } },
  ];

  const fontSizeOptions = [
    { label: '폰트 크기', value: '', disabled: true },
    { label: '9 pt', value: '9pt', style: { fontSize: '9pt' } },
    { label: '10 pt', value: '10pt', style: { fontSize: '10pt' } },
    { label: '12 pt', value: '12pt', style: { fontSize: '12pt' } },
    { label: '14 pt', value: '14pt', style: { fontSize: '14pt' } },
    { label: '16 pt', value: '16pt', style: { fontSize: '16pt' } },
    { label: '20 pt', value: '20pt', style: { fontSize: '20pt' } },
    { label: '24 pt', value: '24pt', style: { fontSize: '24pt' } },
    { label: '30 pt', value: '30pt', style: { fontSize: '30pt' } },
    { label: '36 pt', value: '36pt', style: { fontSize: '36pt' } },
  ];

  const lineHeightOptions = [
    { label: '줄간격', value: '', disabled: true },
    { label: '1', value: '1' },
    { label: '1.2', value: '1.2' },
    { label: '1.5', value: '1.5' },
    { label: '2', value: '2' },
    { label: '2.5', value: '2.5' },
    { label: '3', value: '3' },
  ];

  return (

    <div className="editor-toolbar">
      {/* 글씨체 선택 (커스텀 드롭다운 적용) */}
      <EditorDropdown
        options={fontFamilyOptions}
        value={editor.getAttributes('textStyle').fontFamily || 'Malgun Gothic'}
        onChange={(val) => editor.chain().focus().setFontFamily(val).run()}
        placeholder="글씨체"
        width="110px"
        listWidth="150px"
        listHeight="300px"
        className="font-family-dropdown"
      />

      {/* 폰트 크기 선택 (커스텀 드롭다운 적용) */}
      <EditorDropdown
        options={fontSizeOptions}
        value={String(editor.getAttributes('textStyle').fontSize || '10pt')}
        onChange={(val) => editor.chain().focus().setMark('textStyle', { fontSize: val }).run()}
        placeholder="폰트 크기"
        width="65px"
        listWidth="145px"
        listHeight="270px"
        className="font-size-dropdown"
      />
      {/* 줄간격 선택 (커스텀 드롭다운 적용) */}
      <EditorDropdown
        options={lineHeightOptions}
        value={String(editor.getAttributes('paragraph').lineHeight || '1.5')}
        onChange={(val) => editor.chain().focus().setLineHeight(parseFloat(val)).run()}
        placeholder="줄간격"
        width="80px"
        listWidth="100px"
        listHeight="250px"
        className="line-height-dropdown"
      />

      {/* 글자 색상 선택 */}
      <input
        type="color"
        onChange={(e) =>
          editor.chain().focus().setColor(e.target.value).run()
        }
        title="글자 색상 선택"
      />
      <span className="toolbar-separator">|</span>

      <button
        type="button"
        className={`editor-button ${editor.isActive('bold') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="굵게"
      >
        <i className="fas fa-bold"></i>
      </button>

      <button
        type="button"
        className={`editor-button ${editor.isActive('italic') ? 'active' : ''}`}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="기울임"
      >
        <i className="fas fa-italic"></i>
      </button>
      <span className="toolbar-separator">|</span>
      {/* 왼쪽 정렬 */}
      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title="왼쪽 정렬"
      >
        <i className="fas fa-align-left"></i>
      </button>

      {/* 가운데 정렬 */}
      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title="가운데 정렬"
      >
        <i className="fas fa-align-center"></i>
      </button>

      {/* 오른쪽 정렬 */}
      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title="오른쪽 정렬"
      >
        <i className="fas fa-align-right"></i>
      </button>
      <span className="toolbar-separator">|</span>

      <button
        type="button"
        ref={imageButtonRef}
        className="editor-button"
        onClick={() => setShowImagePanel(!showImagePanel)}
        title="이미지 삽입"
      >
        <i className="fas fa-image"></i>
      </button>

      {showImagePanel && (
        <div
          className="image-insert-panel"
          style={{
            top: `${imagePanelStyle.top}px`,
            left: `${imagePanelStyle.left}px`,
          }}
        >
          {/* 삽입 유형 선택 */}
          <div className="insert-type">
            <label>
              <input
                type="radio"
                name="insertType"
                value="file"
                checked={imageType === 'file'}
                onChange={() => setImageType('file')}
              />
              파일
            </label>
            <label>
              <input
                type="radio"
                name="insertType"
                value="link"
                checked={imageType === 'link'}
                onChange={() => setImageType('link')}
              />
              링크
            </label>
          </div>

          {/* 파일 업로드 */}
          {imageType === 'file' && (
            <div className="input-group">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
              />
            </div>
          )}

          {/* 링크 입력 */}
          {imageType === 'link' && (
            <div className="input-group">
              <input
                type="text"
                placeholder="이미지 링크 입력"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="link-input"
              />
            </div>
          )}

          {/* 사이즈 입력 */}
          <div className="size-inputs">
            <input
              type="text"
              placeholder="가로(px)"
              value={imageSize.width}
              onChange={(e) =>
                setImageSize({ ...imageSize, width: e.target.value })
              }
            />
            <input
              type="text"
              placeholder="세로(px)"
              value={imageSize.height}
              onChange={(e) =>
                setImageSize({ ...imageSize, height: e.target.value })
              }
            />
          </div>

          {/* 삽입 버튼 */}
          <button className="insert-button" onClick={insertImage}>
            이미지 삽입
          </button>
        </div>
      )}



      <button
        ref={tableButtonRef} // 추가
        type="button"
        className="editor-button"
        onClick={() => setShowTableSelector(!showTableSelector)}
        title="테이블 삽입"
      >
        <i className="fas fa-table"></i>
      </button>
      {/* 📋 버튼 아래에 추가 */}
      {showTableSelector && (
        <div className="table-selector" style={{ position: 'absolute', ...selectorStyle }}>
          {[...Array(6)].map((_, rowIndex) => (
            <div key={rowIndex} className="table-row">
              {[...Array(6)].map((_, colIndex) => (
                <div
                  key={colIndex}
                  className={`table-cell ${rowIndex < rows && colIndex < cols ? 'selected' : ''}`}
                  onMouseEnter={() => {
                    setRows(rowIndex + 1);
                    setCols(colIndex + 1);
                  }}
                  onClick={() => {
                    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                    setShowTableSelector(false);
                    setRows(0);
                    setCols(0);
                  }}
                />
              ))}
            </div>
          ))}
          <div className="table-size-label">{rows} × {cols}</div>
        </div>
      )}

      <button type="button" className="editor-button" onClick={() => editor.chain().focus().mergeCells().run()} title="셀 병합">
        <i className="fas fa-object-group"></i>
      </button>
      <button type="button" className="editor-button" onClick={() => editor.chain().focus().splitCell().run()} title="셀 분리">
        <i className="fas fa-columns"></i>
      </button>
      <button
        type="button"
        className="editor-button"
        onClick={() => {
          const url = window.prompt('링크 주소를 입력하세요');
          if (url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }
        }}
        title="링크 삽입"
      >
        <i className="fas fa-link"></i>
      </button>

      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().unsetLink().run()}
        title="링크 제거"
      >
        <i className="fas fa-unlink"></i>
      </button>
      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().undo().run()}
        title="실행 취소"
      >
        <i className="fas fa-undo"></i>
      </button>

      <button
        type="button"
        className="editor-button"
        onClick={() => editor.chain().focus().redo().run()}
        title="다시 실행"
      >
        <i className="fas fa-redo"></i>
      </button>

    </div>
  );
}

export default EditorToolbar;